/**
 * Reditus referral widget loader.
 * The Reditus tracking script (which provides window.gr) is loaded via GTM in production
 * and via a <script> tag in public/index.html for local development.
 * Docs: https://docs.getreditus.com/referral-program/loading
 * Outside production this module falls back to mock mode when window.gr is unavailable;
 * in production it never mocks, so a late-arriving window.gr is picked up on the next attempt.
 */
import config from '@/config'

let widgetReady = false
let mockMode = false

const isReditusConfigured = () => !!config.reditusProductId

export const isMockAllowed = () => !config.isProductionBuild

/**
 * Ensures the Reditus tracking script (window.gr) is available.
 * The tracking script should already be installed on the page (via index.html or GTM).
 * Outside production, enters mock mode when the product id is unset or window.gr is missing.
 */
export const loadReditusScript = () => {
  const shouldMock = isMockAllowed() && (!isReditusConfigured() || !window.gr)
  if (shouldMock) {
    mockMode = true
    widgetReady = true
  }
  return Promise.resolve()
}

export const initReditusWidget = (productId, authToken, userDetails) => {
  if (mockMode) {
    widgetReady = true
    return true
  }
  if (!window.gr) return false
  window.gr('loadReferralWidget', {
    product_id: productId,
    auth_token: authToken,
    user_details: userDetails,
  })
  widgetReady = true
  return true
}

const shadowHostsInBody = () => [...document.body.children].filter((element) => element.shadowRoot)

const WIDGET_HOST_WAIT_MS = 5000

// The widget is English-only but inherits the page direction through the shadow host it appends to <body>.
const keepNewWidgetHostsLeftToRight = (hostsBeforeShow) => {
  const markNewHosts = () => {
    const newHosts = shadowHostsInBody().filter((host) => !hostsBeforeShow.includes(host))
    newHosts.forEach((host) => host.setAttribute('dir', 'ltr'))
    return newHosts.length > 0
  }
  if (markNewHosts()) return
  const observer = new MutationObserver(() => {
    if (markNewHosts()) observer.disconnect()
  })
  observer.observe(document.body, { childList: true })
  setTimeout(() => observer.disconnect(), WIDGET_HOST_WAIT_MS)
}

export const showReditusWidget = () => {
  if (mockMode) return 'mock'
  if (window.referralWidget) {
    const hostsBeforeShow = shadowHostsInBody()
    window.referralWidget.show()
    keepNewWidgetHostsLeftToRight(hostsBeforeShow)
    return true
  }
  return false
}

export const isWidgetReady = () => widgetReady
export const isMockMode = () => mockMode

export const resetState = () => {
  widgetReady = false
  mockMode = false
}
